// Implements: F-UIX-019, F-GEN-036
/**
 * What picking a model of the provider catalogue does to the form (mockup v3 P7, v3 `pickModel`),
 * as pure functions.
 *
 * The tier takes the model; its prices are filled from the catalogue (micros per million tokens,
 * written as the admin would type them, `0` kept as a real free price) and tagged «del catálogo»;
 * typing over one of them turns its tag into «editado». A model the catalogue gives no price for
 * keeps whatever price the form already holds for it, untagged, and the caller says so. The output
 * cap is clamped to the model's maximum when it is above it ("Sin límite" stays as it is), and a
 * model that does not reason loses its reasoning effort. Nothing here is sent: the save still
 * parses the form, and the server checks the model against its catalogue again.
 */

import { PLATFORM_LOCALE } from "../../../types/locale";
import type { CatalogueModelView } from "../../../types/modelCatalogue";
import type { TierName } from "../../../types/modelSettings";
import { priceDraftOf } from "./modelSettingsForm";
import type { SettingsDraft, TierDraft } from "./modelSettingsForm";

export type PriceTag = "catalogue" | "edited";

export interface CatalogueFill {
  readonly draft: SettingsDraft;
  /** Whether the catalogue gave a price to fill (`false`: the form's own price was kept). */
  readonly priceFilled: boolean;
}

const WHOLE_RE = /^\d+$/;
const MILLION = 1_000_000;
const THOUSAND = 1_000;
/** v3 `fmtTok`: a million tokens is written in binary megas (1 048 576 is "1 M"). */
const MEGA = 1_048_576;
const NO_VALUE = "—";

function clampedCap(tier: TierDraft, maximum: number | null): string {
  if (tier.noLimit || maximum === null) return tier.cap;
  const typed = tier.cap.trim();
  if (!WHOLE_RE.test(typed)) return tier.cap;
  return Number(typed) > maximum ? String(maximum) : tier.cap;
}

export function fillFromCatalogue(draft: SettingsDraft, tier: TierName, model: CatalogueModelView): CatalogueFill {
  const current = draft[tier];
  const next: TierDraft = {
    model: model.model_id,
    cap: clampedCap(current, model.max_output_tokens),
    noLimit: current.noLimit,
    effort: model.supports_reasoning ? current.effort : "",
  };
  if (model.price === null) {
    const tags = { ...draft.priceTags };
    Reflect.deleteProperty(tags, model.model_id);
    return { draft: { ...draft, [tier]: next, priceTags: tags }, priceFilled: false };
  }
  return {
    draft: {
      ...draft,
      [tier]: next,
      prices: { ...draft.prices, [model.model_id]: priceDraftOf(model.price) },
      priceTags: { ...draft.priceTags, [model.model_id]: "catalogue" },
    },
    priceFilled: true,
  };
}

/** The admin types one price: a catalogue one becomes «editado». */
export function editPrice(draft: SettingsDraft, model: string, part: "prompt" | "completion", value: string): SettingsDraft {
  const prices = { ...draft.prices, [model]: { prompt: "", completion: "", ...draft.prices[model], [part]: value } };
  if (draft.priceTags?.[model] === undefined) return { ...draft, prices };
  return { ...draft, prices, priceTags: { ...draft.priceTags, [model]: "edited" } };
}

export function priceTagOf(draft: SettingsDraft, model: string): PriceTag | undefined {
  return draft.priceTags?.[model];
}

const DOLLARS = new Intl.NumberFormat(PLATFORM_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A catalogue price per million tokens, in dollars with two decimals ("3.00", v3 `money`). */
export function catalogueDollars(micros: number): string {
  return DOLLARS.format(micros / MILLION);
}

/** A token count as the picker writes it: "200 K", "1 M"; "—" when the catalogue gives none. */
export function tokenText(tokens: number | null): string {
  if (tokens === null) return NO_VALUE;
  if (tokens >= MILLION) return `${String(Math.round(tokens / MEGA))} M`;
  return `${String(Math.round(tokens / THOUSAND))} K`;
}
